import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from '../lib/offlineFirestore';
import { db } from '../config/firebase';
import serverModeService from './serverModeService';
import { COLLECTIONS } from '../constants/collections';
import { MODULE_CATALOG, DEFAULT_CORE_MODULES, isModuleActive } from '../constants/moduleCatalog';

const safe = (value, fallback) => {
  const clean = typeof value === 'string' ? value.trim() : '';
  return clean || fallback;
};

const TENANTS = safe(COLLECTIONS?.TENANTS, 'tenants');
const SUBSCRIPTIONS = safe(COLLECTIONS?.SUBSCRIPTIONS, 'subscriptions');
const PLANS = safe(COLLECTIONS?.PLANS, 'plans');
const ADDON_REQUESTS = safe(COLLECTIONS?.ADDON_REQUESTS, 'addonRequests');

const idValue = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    const clean = value.trim();
    return clean.includes('/')
      ? clean.split('/').filter(Boolean).pop() || ''
      : clean;
  }
  if (value?.id) return String(value.id);
  if (value?.path) {
    return String(value.path).split('/').filter(Boolean).pop() || '';
  }
  return '';
};

const toDate = (value) => {
  if (!value) return null;

  const date =
    value?.toDate?.() ||
    (value instanceof Date ? value : new Date(value));

  return Number.isNaN(date?.getTime?.()) ? null : date;
};

const subscriptionMatches = (data, tenantId) =>
  [
    idValue(data?.tenantId),
    data?.tenantIdValue,
    idValue(data?.companyId),
  ].some((value) => value && String(value) === String(tenantId));

const normalizeStatus = (value) =>
  String(value || '').trim().toLowerCase();


const ACCESS_CACHE_TTL_MS = 60 * 1000;
const accessCache = new Map();
const moduleAccessCache = new Map();
const subscriptionCache = new Map();
const inFlight = new Map();

// The same tenant can have separate authenticated LAN and cloud snapshots.
// Never reuse a cached entitlement from one source in the other source.
const scopedKey = (tenantId) => `${serverModeService.getRoutingScope()}::${idValue(tenantId)}`;

const cachedValue = (cache, key) => {
  const entry = cache.get(String(key || ''));
  if (!entry) return null;
  if (Date.now() - entry.at > ACCESS_CACHE_TTL_MS) {
    cache.delete(String(key || ''));
    return null;
  }
  return entry.value;
};

const cachedRequest = async (cache, requestKey, key, loader, force = false) => {
  const normalized = String(key || '');
  if (!force) {
    const hit = cachedValue(cache, normalized);
    if (hit) return hit;
    if (inFlight.has(requestKey)) return inFlight.get(requestKey);
  }
  const request = Promise.resolve().then(loader);
  inFlight.set(requestKey, request);
  try {
    const value = await request;
    cache.set(normalized, { at: Date.now(), value });
    return value;
  } finally {
    inFlight.delete(requestKey);
  }
};

class SubscriptionAccessService {
  peekTenantAccess(tenantId) {
    return cachedValue(accessCache, scopedKey(tenantId));
  }

  peekTenantModuleAccess(tenantId) {
    return cachedValue(moduleAccessCache, scopedKey(tenantId));
  }

  clearTenantCache(tenantId) {
    const key = idValue(tenantId);
    if (!key) return;
    for (const cache of [accessCache, moduleAccessCache, subscriptionCache]) {
      for (const cachedKey of cache.keys()) {
        if (cachedKey === key || cachedKey.endsWith(`::${key}`)) cache.delete(cachedKey);
      }
    }
  }

  async getTenantAccess(tenantId, { force = false } = {}) {
    const key = idValue(tenantId);
    if (!key) return this._fetchTenantAccess(tenantId);
    return cachedRequest(accessCache, `access:${scopedKey(key)}`, scopedKey(key), () => this._fetchTenantAccess(key), force);
  }

  async getTenantModuleAccess(tenantId, { force = false } = {}) {
    const key = idValue(tenantId);
    if (!key) return this._fetchTenantModuleAccess(tenantId);
    return cachedRequest(moduleAccessCache, `modules:${scopedKey(key)}`, scopedKey(key), () => this._fetchTenantModuleAccess(key), force);
  }

  async getCurrentSubscription(tenantId, { force = false } = {}) {
    const key = idValue(tenantId);
    if (!key) return this._fetchCurrentSubscription(tenantId);
    return cachedRequest(subscriptionCache, `subscription:${scopedKey(key)}`, scopedKey(key), () => this._fetchCurrentSubscription(key), force);
  }
  async _fetchTenantAccess(tenantId) {
    if (!tenantId) {
      return {
        allowed: false,
        reason: 'missing_tenant',
        message: 'No company is linked to this account.',
        tenant: null,
        subscription: null,
      };
    }

    const tenantSnapshot = await getDoc(doc(db, TENANTS, tenantId));

    if (!tenantSnapshot.exists()) {
      return {
        allowed: false,
        reason: 'tenant_not_found',
        message: 'Company profile was not found.',
        tenant: null,
        subscription: null,
      };
    }

    const tenant = {
      id: tenantSnapshot.id,
      ...tenantSnapshot.data(),
    };

    const status = normalizeStatus(tenant.status);

    if (
      tenant.loginBlocked === true ||
      tenant.loginEnabled === false ||
      ['blocked', 'suspended', 'disabled', 'archived', 'inactive'].includes(status)
    ) {
      return {
        allowed: false,
        reason: 'company_blocked',
        message:
          tenant.loginBlockedReason ||
          'Company login has been blocked by the system administrator.',
        tenant,
        subscription: null,
      };
    }

    const subscription = await this.getCurrentSubscription(tenantId);

    const tenantSubscriptionStatus =
      normalizeStatus(tenant.subscriptionStatus);

    const effectiveStatus =
      tenantSubscriptionStatus ||
      normalizeStatus(subscription?.status);

    const endDate =
      toDate(tenant.subscriptionEndAt) ||
      toDate(subscription?.endDate) ||
      toDate(subscription?.endAt);

    const expiredByDate =
      Boolean(endDate && endDate.getTime() <= Date.now());

    const expiredByStatus =
      ['expired', 'cancelled', 'terminated'].includes(effectiveStatus);

    if (expiredByDate || expiredByStatus) {
      return {
        allowed: false,
        reason: 'subscription_expired',
        message:
          `Subscription expired${endDate ? ` on ${endDate.toLocaleDateString('en-IN')}` : ''}. ` +
          'Please contact the administrator to renew the plan.',
        tenant,
        subscription,
        endDate,
      };
    }

    if (
      subscription &&
      ['suspended', 'payment_failed'].includes(
        normalizeStatus(subscription.status)
      )
    ) {
      return {
        allowed: false,
        reason: 'subscription_suspended',
        message:
          'Subscription access is suspended. Please contact the administrator.',
        tenant,
        subscription,
        endDate,
      };
    }

    return {
      allowed: true,
      reason: 'active',
      message: '',
      tenant,
      subscription,
      endDate,
    };
  }


  async _fetchTenantModuleAccess(tenantId) {
    const tenantKey = idValue(tenantId);
    if (!tenantKey) {
      return { strict: false, legacyOpen: true, activeModules: [], planModules: [], assignedModules: [], addonModules: [] };
    }

    const tenantSnapshot = await getDoc(doc(db, TENANTS, tenantKey));
    if (!tenantSnapshot.exists()) {
      return { strict: false, legacyOpen: true, activeModules: [], planModules: [], assignedModules: [], addonModules: [] };
    }

    const tenant = { id: tenantSnapshot.id, ...tenantSnapshot.data() };
    const strict = tenant.moduleManagementEnabled === true;

    // Backward compatibility: existing production tenants keep their current
    // modules until Super Admin explicitly saves module assignments.
    if (!strict) {
      return {
        strict: false,
        legacyOpen: true,
        tenant,
        activeModules: MODULE_CATALOG.map((item) => item.key),
        assignedModules: [],
        planModules: [],
        addonModules: [],
        defaultCoreModules: [...DEFAULT_CORE_MODULES],
      };
    }

    const assignedModules = Array.isArray(tenant.assignedModules)
      ? tenant.assignedModules.map((value) => String(value || '').trim()).filter(Boolean)
      : [];

    let planModules = [];
    const currentSubscription = await this.getCurrentSubscription(tenantKey).catch(() => null);
    const planId =
      idValue(tenant.subscriptionPlanId) ||
      tenant.subscriptionPlanIdValue ||
      idValue(currentSubscription?.planId) ||
      currentSubscription?.planIdValue ||
      '';

    if (planId) {
      try {
        const planSnapshot = await getDoc(doc(db, PLANS, planId));
        if (planSnapshot.exists()) {
          const plan = planSnapshot.data() || {};
          planModules = Array.isArray(plan.modules)
            ? plan.modules.map((value) => idValue(value) || String(value || '').trim()).filter(Boolean)
            : Array.isArray(plan.moduleKeys)
              ? plan.moduleKeys.map((value) => String(value || '').trim()).filter(Boolean)
              : [];
        }
      } catch (error) {
        console.warn('Plan module lookup failed:', error?.message || error);
      }
    }

    const addonModules = [];
    try {
      const ref = collection(db, ADDON_REQUESTS);
      const attempts = await Promise.allSettled([
        getDocs(query(ref, where('tenantIdValue', '==', tenantKey))),
        getDocs(query(ref, where('tenantId', '==', tenantKey))),
      ]);
      const rows = new Map();
      attempts.forEach((result) => {
        if (result.status !== 'fulfilled') return;
        result.value.docs.forEach((snapshot) => rows.set(snapshot.id, snapshot.data() || {}));
      });
      rows.forEach((data) => {
        const status = normalizeStatus(data.status);
        const endDate = toDate(data.endDate || data.expiresAt);
        const activeByDate = !endDate || endDate.getTime() > Date.now();
        if (['active', 'approved', 'trialing'].includes(status) && activeByDate) {
          const key = String(data.moduleKey || data.module || '').trim();
          if (key) addonModules.push(key);
        }
      });
    } catch (error) {
      console.warn('Add-on entitlement lookup failed:', error?.message || error);
    }

    const blockedModules = new Set(
      Array.isArray(tenant.blockedModules)
        ? tenant.blockedModules.map((value) => String(value || '').trim()).filter(Boolean)
        : []
    );

    // Strict company-module management means the Super Admin checklist is the
    // visibility source of truth. Subscription/add-on rows are still returned
    // as commercial metadata, but they must not silently activate navigation.
    // This guarantees that a company sees ONLY modules explicitly assigned by
    // Super Admin.
    const activeModules = Array.from(new Set(assignedModules))
      .filter((key) => !blockedModules.has(key));

    return {
      strict: true,
      legacyOpen: false,
      tenant,
      activeModules,
      assignedModules,
      planModules,
      addonModules,
      blockedModules: Array.from(blockedModules),
      defaultCoreModules: [...DEFAULT_CORE_MODULES],
    };
  }

  async canAccessModule(tenantId, moduleKey) {
    const access = await this.getTenantModuleAccess(tenantId);
    return isModuleActive(access, moduleKey);
  }

  async _fetchCurrentSubscription(tenantId) {
    const ref = collection(db, SUBSCRIPTIONS);
    const tenantRef = doc(db, TENANTS, tenantId);

    const attempts = await Promise.allSettled([
      getDocs(query(ref, where('tenantIdValue', '==', tenantId))),
      getDocs(query(ref, where('tenantId', '==', tenantId))),
      getDocs(query(ref, where('tenantId', '==', tenantRef))),
    ]);

    const merged = new Map();

    attempts.forEach((result) => {
      if (result.status !== 'fulfilled') return;
      result.value.docs.forEach((snapshot) => {
        merged.set(snapshot.id, {
          id: snapshot.id,
          ...snapshot.data(),
        });
      });
    });

    const rows = Array.from(merged.values()).sort((a, b) => {
      const aDate =
        toDate(a.endDate || a.endAt || a.updatedAt)?.getTime() || 0;
      const bDate =
        toDate(b.endDate || b.endAt || b.updatedAt)?.getTime() || 0;
      return bDate - aDate;
    });

    return (
      rows.find((item) =>
        ['active', 'trialing', 'past_due'].includes(
          normalizeStatus(item.status)
        )
      ) ||
      rows[0] ||
      null
    );
  }
}

const subscriptionAccessService = new SubscriptionAccessService();
export default subscriptionAccessService;